import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, message, Typography, Select } from 'antd';
import {
	downloadLocationsCSVForBuyer,
	locationsRestCrudThunksForBuyer,
} from '../../thunks/locations_thunks';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { EmptyState } from '../empty_state/EmptyState';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getTupleNameFieldName,
	getTupleValueFieldName,
} from '../../utils/DataAccessUtils';
import { EmptorTag } from '../emptor_tag/EmptorTag';
import { LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/locations_actions';
import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import { buyerRoleUpdateUser } from '../../thunks/buyer_contacts_thunks';
import BuyerRoleUpdateForm from '../buyer_role_update_form/BuyerRoleUpdateForm';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE } from '../../utils/DataConstants';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import OWAsyncTupleSelect from '../ow_async_select/OWAsyncTupleSelect';
import { locationTypesAdditionalFieldsRestCrudThunksForBuyer } from '../../thunks/location_types_additional_fields_thunks';
import { INITIAL_IS_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import { brandsRestCrudThunksForBuyer } from '../../thunks/brands_thunks';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import { DownloadOutlined } from '@ant-design/icons';
import BrandLogoDisplay from '../brands_overview_index_page/BrandLogoDisplay';
import { useFilters } from '../../hooks/useFilters';
import { getLocationFilterConfig } from '../../utils/LocationFilterUtils';
require('./LocationsIndexPage.less');
const { Text } = Typography;

const TC_NAME = 'locationsIndex';
const LOCATIONS_OVERVIEW_TARGET_COLLECTION = 'locationsIndex';
const LOCATIONS_ADVANCED_FILTERS = 'locationsAdvancedFilters';

const LocationsIndexPage: FC<any> = ({
	history,
	location,
	locations,
	updateLocationsFilters,
	clearAndUpdateFilters,
	currentUser,
	roleUpdateUser,
	exportLocations,
	fetchLocationTypesAdditionalFields,
	fetchMultipleLocationTypesAdditionalFields,
	locationTypesAdditionalFields,
	fetchBrandsCount,
}) => {
	const [displayMode, setDisplayMode] = useState('list');
	const [exporting, setExporting] = useState(false);
	const [roleUpdateVisible, setRoleUpdateVisible] = useState(false);
	const [userContact, setUserContact] = useState<any>(null);
	const formRefs = React.useRef<any>({ roleUpdateForm: null });

	const [brandAvailable, setBrandAvailable] = useState(false);

	useEffect(() => {
		fetchBrandsCount()
			.then((count) => setBrandAvailable(count > 0))
			.catch(() => {});
	}, []);

	const locationFilters = useFilters({
		filterTargetCollectionName: LOCATIONS_ADVANCED_FILTERS,
		overviewCollectionName: LOCATIONS_OVERVIEW_TARGET_COLLECTION,
		updateFilters: useCallback(
			(filters) => updateLocationsFilters(filters, LOCATIONS_OVERVIEW_TARGET_COLLECTION),
			[updateLocationsFilters]
		),
	});

	const additionalFieldsFilter = useMemo(
		() => ({
			label: 'Additional Fields',
			fieldName: FILTER_CONFIG_NAMES.ADDITIONAL_FIELD,
			type: FILTER_FIELD_TYPE.TUPLE,
			stateSlice: locationTypesAdditionalFields,
			filtersValueAccessor: (filtersStateSlice) => {
				const name = filtersStateSlice[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				const value =
					filtersStateSlice[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				return [name, value];
			},
			handleChange: (values) => {
				updateLocationsFilters(
					{
						[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
							'0',
							values,
							undefined
						),
						[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
							'1',
							values,
							undefined
						),
					},
					LOCATIONS_OVERVIEW_TARGET_COLLECTION
				);
			},
			targetCollectionName: LOCATIONS_ADVANCED_FILTERS,
			fetch: fetchLocationTypesAdditionalFields,
			fetchMultiple: fetchMultipleLocationTypesAdditionalFields,
			renderRecord: (entity) => (
				<Select.Option key={entity.id} value={entity.id}>
					<div className="flex flex-row items-center">
						<div>{entity.name}</div>
					</div>
				</Select.Option>
			),
			valueAccessor: (item) => item.name,
			component: OWAsyncTupleSelect,
		}),
		[
			locationTypesAdditionalFields,
			updateLocationsFilters,
			fetchLocationTypesAdditionalFields,
			fetchMultipleLocationTypesAdditionalFields,
		]
	);

	const filterConfig = useMemo(
		() =>
			getLocationFilterConfig({
				filters: locationFilters,
				brandAvailable,
				additionalFieldsFilter,
			}),
		[locationFilters, brandAvailable, additionalFieldsFilter]
	);

	const saveFormRef = useCallback(
		(formName) => (formRef) => {
			formRefs.current[formName] = formRef;
		},
		[]
	);

	const showRoleUpdateModal = useCallback(
		(record) => (e) => {
			setRoleUpdateVisible(true);
			setUserContact({
				contact: { ...record },
				role: record['roles'],
				locationIds: record['locationIds'] || [],
			});
		},
		[]
	);

	const handleBuyerRoleUpdateSubmit = useCallback(() => {
		const form = formRefs.current['roleUpdateForm'].props.form;
		form.validateFields((err, values) => {
			if (err) return;
			const updateRole = {
				userId: values.email,
				roles: [values.roles],
				locationIds: values.locationIds,
			};
			roleUpdateUser(updateRole).then(() => {
				setRoleUpdateVisible(false);
				form.resetFields();
				message.success('Location Access Granted');
				history.push('/buyer/locations/overview/all');
			});
		});
	}, [roleUpdateUser, history]);

	const handleBuyerRoleUpdateCancel = useCallback(() => {
		setRoleUpdateVisible(false);
	}, []);

	const downloadCSV = useCallback(() => {
		const filters = nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {});
		setExporting(true);
		exportLocations(filters)
			.catch((err) => message.error(err))
			.finally(() => setExporting(false));
	}, [locations, exportLocations]);

	const popupRenderFunc = useCallback(
		(record) => `
            <div>
                <a href="/buyer/locations/detail/${record.id}">
                    <div class="locationsIndexPage__row" style="align-items: flex-start;">
                        <div style="margin-right: 24px;">
                            <div class="locationsIndexPage__rowTitle">${record.name}</div>
                            <div class="locationsIndexPage__rowSubtitle">
                                ${nullSafeGet(
																	'buyerFacility.primaryAddress.streetAddress1',
																	record
																)}
                            </div>
                        </div>
                        <div class="emptorTag locationsIndexPage__pill">
                            ${
															record.totalNotClosedWOCount > 0
																? record.totalNotClosedWOCount + ' Open WOs'
																: 'No Open WOs'
														}
                        </div>
                    </div>
                    <div class="locationsIndexPage__rowSubtitle">
                        <div class="locationsIndexPage__rowInlineGroup">
                            ${nullSafeGet(
															'buyerFacility.primaryAddress.city',
															record
														)}, ${nullSafeGet(
			'buyerFacility.primaryAddress.region',
			record
		)} ${nullSafeGet('buyerFacility.primaryAddress.postcode', record)}
                        </div>
                    </div>
                </a>
            </div>
        `,
		[]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				defaultSortOrder: 'ascend',
				render: (text, record) => (
					<div className="locationsIndexPage__row">
						<div>
							<div className="flex items-center">
								<BrandLogoDisplay brand={nullSafeGet('brand', record)} />
								<div>
									<div className="locationsIndexPage__rowTitle">
										<Ellipsis tooltip={true} length={64}>
											{record.name}
										</Ellipsis>
									</div>
									<div className="locationsIndexPage__rowSubtitle">
										<div className="locationsIndexPage__rowInlineGroup">
											{nullSafeGet('buyerFacility.primaryAddress.city', record)}
										</div>
										<div className="locationsIndexPage__rowInlineGroup2">
											{nullSafeGet('locationType.name', record)} {nullSafeGet('storeId', record)}
										</div>
										{record.isActive ? null : (
											<div className="locationsIndexPage__rowInlineGroup">
												<Text className={'mr-1'} type={'danger'}>
													Inactive
												</Text>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="flex items-end">
							<EmptorTag className="locationsIndexPage__pill">
								{record.totalNotClosedWOCount > 0
									? record.totalNotClosedWOCount + ' Open WOs'
									: 'No Open WOs'}
							</EmptorTag>
						</div>
					</div>
				),
			},
		],
		[]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/buyer/locations/detail/${nullSafeGet(
					'id',
					record
				)}/details?targetCollectionName=${LOCATIONS_OVERVIEW_TARGET_COLLECTION}`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
						preserve: true,
					})
				);
			},
		}),
		[history, location]
	);

	return (
		<div className="locationsIndexPage">
			<BuyerRoleUpdateForm
				wrappedComponentRef={saveFormRef('roleUpdateForm')}
				visible={roleUpdateVisible}
				formData={userContact}
				onCancel={handleBuyerRoleUpdateCancel}
				onSubmit={handleBuyerRoleUpdateSubmit}
				isLocationChange={true}
			/>

			<ReduxDataTable
				filterConfig={filterConfig}
				rightActions={
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Button
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							loading={exporting}
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xl">Export CSV</span>
						</Button>
						<Button.Group size="large">
							<Button onClick={() => setDisplayMode('list')} disabled={displayMode === 'list'}>
								<i className="icons8-font icons8-bulleted-list-2" />
							</Button>
							<Button onClick={() => setDisplayMode('map')} disabled={displayMode === 'map'}>
								<i className="icons8-font icons8-location-marker" />
							</Button>
						</Button.Group>
						{currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
							<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
								<OWButtonWithSupplierManagedLock
									size="large"
									type="primary"
									onClick={() => history.push('/buyer/locations/overview/new')}
								>
									Add Location
								</OWButtonWithSupplierManagedLock>
							</AccessPermissionChecker>
						) : null}
					</div>
				}
				mode={displayMode}
				handleQueryParams
				longitudeAccessor={(el) => nullSafeGet('buyerFacility.longitude', el)}
				latitudeAccessor={(el) => nullSafeGet('buyerFacility.latitude', el)}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Looks like your team has not added any locations yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)) ? (
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										Looks like your team has not added any locations yet.
									</div>
									<OWButtonWithSupplierManagedLock
										type="primary"
										onClick={() => history.push('/buyer/locations/overview/new')}
									>
										Add your first location
									</OWButtonWithSupplierManagedLock>
								</div>
							) : (
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										Looks like your team has not setup access for you yet.
									</div>
									<Button type="primary" onClick={showRoleUpdateModal(currentUser)}>
										Request access to my location
									</Button>
								</div>
							)
						}
					/>
				}
				entityCollectionName="locations"
				targetCollectionName={TC_NAME}
				tableColumns={columns}
				popupRenderFunc={popupRenderFunc}
				showHeader={false}
				keyAccessor={(el) => el.id}
				onTableRow={onRow}
				preAppliedFilters={{ ...INITIAL_IS_ACTIVE_FILTERS }}
				fetchData={locationsRestCrudThunksForBuyer}
				stateSlice={locations}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	locations: state.locations,
	currentUser: state.session.currentUser,
	locationTypesAdditionalFields: state.location_types_additional_fields,
});

const mapDispatchToProps = (dispatch) => ({
	updateLocationsFilters: (filters, targetCollection) =>
		dispatch(LOCATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(LOCATIONS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	roleUpdateUser: (entity) => dispatch(buyerRoleUpdateUser(entity)),
	fetchMultipleLocationTypesAdditionalFields: (ids, targetCollectionName) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchLocationTypesAdditionalFields: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForBuyer.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	exportLocations: (params, filters) => dispatch(downloadLocationsCSVForBuyer(params, filters)),
	fetchBrandsCount: (params) => dispatch(brandsRestCrudThunksForBuyer.countBy(params, 'all')),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LocationsIndexPage));
